/* @HEADER
 * @version 1.1.964 | 2026-10-26
 * @purpose Turns a link into where to go on this device: the space the device holds for the address, the board to bring into view, not-here-yet for a space the server grants and the device lacks, and the keys the device holds a space under.
 * @sidecar navLinkLanding.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8358, ZVX-DEV-8265, ZVX-DEV-7134

/**
 * The second stage of opening a link (ZVX-4464): the project is the one on screen, the server has said the person may see the
 * space, and now the link is turned into where to go on THIS device -- which of the spaces the device holds, and which board
 * of it to bring into view. Pure: the spaces are handed in.
 *
 * A space the server grants and the device does not hold yet is `not-here-yet`, not «недоступно»: the person may see it, the
 * copy has not arrived. It is told apart so the person is not told they have no right when they have.
 */

import type { NavLink } from './navLinks.ts';
import { pickCanonicalSpaceRow } from '../lib/projectSpaceRegistryReconcile.ts';

export interface LandingSpace {
  id: string;
  /** The server's id for the space, once it has one. */
  serverSpaceId?: string | null;
  /** When the row was made: the reconcile's rule for which copy of one server space survives reads it (ZVX-DEV-8358). */
  created_at?: string | null;
}

export type Landing =
  | { kind: 'project' }
  | { kind: 'space'; spaceId: string; boardId: string | null }
  | { kind: 'not-here-yet' };

/** The local space an address names: by the server's id, else by the local id (the built-in space is `default`). */
function spaceNamed(spaces: readonly LandingSpace[], spaceId: string): LandingSpace | null {
  // A device can hold one server space twice (the row the catalog minted and the registry's placeholder). The link lands on the copy
  // the reconcile keeps -- the one the boards are re-hung on -- by the reconcile's own rule, not on the first of the list (ZVX-DEV-8358).
  const copies = spaces.filter((space) => space.serverSpaceId === spaceId);
  if (copies.length > 0) return pickCanonicalSpaceRow(copies);
  return spaces.find((space) => space.id === spaceId) ?? null;
}

export function planLanding(link: NavLink, spaces: readonly LandingSpace[]): Landing {
  if (link.kind === 'project') return { kind: 'project' };
  // An organisation opens nothing (the resolver has already said so). A card is opened by a later slice; until then its link
  // brings the person to the card's space, and to its board when the link names one, never to nothing.
  if (link.kind === 'organisation') return { kind: 'not-here-yet' };
  const space = spaceNamed(spaces, link.spaceId);
  if (space === null) return { kind: 'not-here-yet' };
  const boardId = link.kind === 'board' || link.kind === 'card' ? link.boardId : null;
  return { kind: 'space', spaceId: space.id, boardId };
}

/**
 * The card a link asks to open once the person has landed (ZVX-DEV-8332, ZVX-4645): its sync id, and the board the link names (none: the
 * card is looked for on every board of the space). Apart from `planLanding` on purpose -- where the person lands is the space and the
 * board whether or not the card can be opened.
 */
export function cardToOpen(link: NavLink): { syncId: string; boardId: string | null } | null {
  return link.kind === 'card' ? { syncId: link.cardId, boardId: link.boardId } : null;
}

/** The keys this device holds a space under, for the server's question (`navLinkSpaceRight`): the local id of every copy of it. */
export function localKeysForSpace(spaces: readonly LandingSpace[], spaceServerId: string): string[] {
  return spaces
    .filter((space) => space.serverSpaceId === spaceServerId && space.id !== 'default')
    .map((space) => space.id);
}
